import { useCallback, useRef } from "react";
import { useLazyQuery } from "@apollo/client";

import { LOAD_LOCATION_RULE_NOTES_QUERY } from "globals/graphql";
import { useOperator } from "globals/hooks";
import { PlanVariant } from "types";

const isPro = (plan?: PlanVariant | null): boolean =>
  plan === PlanVariant.Pro || plan === PlanVariant.Vip;

type UseLocationRuleNotesReturn = {
  evaluateLocationNotes: (
    pickupAddress: string,
    dropoffAddress: string,
    dateTime?: string
  ) => Promise<{ tripNote: string | null } | null>;
};

export const useLocationRuleNotes = (): UseLocationRuleNotesReturn => {
  const { operator } = useOperator();

  const [fetchNotes] = useLazyQuery(LOAD_LOCATION_RULE_NOTES_QUERY, {
    fetchPolicy: "network-only",
  });

  const lastEvaluatedRef = useRef<string>("");

  const evaluateLocationNotes = useCallback(
    async (pickupAddress: string, dropoffAddress: string, dateTime?: string) => {
      if (!isPro(operator?.plan)) return null;
      if (!pickupAddress || !dropoffAddress) return null;

      const cacheKey = `${pickupAddress}|${dropoffAddress}|${dateTime || ""}`;
      if (cacheKey === lastEvaluatedRef.current) return null;

      const { data } = await fetchNotes({
        variables: {
          pickup: { location: pickupAddress },
          dropoff: { location: dropoffAddress },
          dateTime: dateTime || undefined,
        },
      });

      lastEvaluatedRef.current = cacheKey;

      return data?.loadLocationRuleNotes || null;
    },
    [fetchNotes, operator?.plan]
  );

  return { evaluateLocationNotes };
};
